/**
 * AutoFillNotification Component
 * Shows user that form was auto-filled from order details
 * @author SAUBHAGYA Development Team
 */

import React, { useState } from 'react';
import { AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export interface AutoFillNotificationProps {
  orderNumber: string;
  orderId: number;
  autoFilledFields: string[];
  onViewOrder?: () => void;
}

export function AutoFillNotification({
  orderNumber,
  orderId,
  autoFilledFields,
  onViewOrder,
}: AutoFillNotificationProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="space-y-3 mb-6">
      {/* Main Alert */}
      <Alert className="bg-blue-50 border-blue-200">
        <AlertCircle className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold">
                ✓ Form auto-filled from Order #{orderNumber}
              </p>
              <p className="text-sm text-blue-700 mt-1">
                {autoFilledFields.length} fields populated from order details. Please review and edit as needed.
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="text-blue-600 hover:text-blue-700"
            >
              {expanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </AlertDescription>
      </Alert>

      {/* Expanded Details */}
      {expanded && (
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="space-y-3">
            <div>
              <p className="text-sm font-semibold text-slate-700 mb-2">
                Auto-Filled Fields ({autoFilledFields.length}):
              </p>
              <div className="flex flex-wrap gap-2">
                {autoFilledFields.map(field => (
                  <Badge
                    key={field}
                    variant="secondary"
                    className="bg-blue-100 text-blue-800"
                  >
                    ✓ {field}
                  </Badge>
                ))}
              </div>
            </div>

            {onViewOrder && (
              <Button
                variant="outline"
                size="sm"
                onClick={onViewOrder}
                className="w-full gap-2 mt-2"
              >
                📋 View Original Order Details
              </Button>
            )}

            <p className="text-xs text-slate-600 italic">
              All fields can be edited before submitting. Changes will not affect the original order.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}
